import "./topic-content-expansions";
import { OZK_TOPICS, type TopicDefinition, type TopicStage } from "./topic-taxonomy";

const stage = (
  id: string,
  label: string,
  labelEn: string,
  description: string,
  descriptionEn: string,
): TopicStage => ({ id, label, labelEn, description, descriptionEn });

const commonStages = [
  stage("researching", "알아보는 중", "Researching", "공식 정보와 자신의 상황을 비교하는 단계", "Comparing official information with your situation"),
  stage("preparing", "준비 중", "Preparing", "서류와 조건을 준비하는 단계", "Preparing documents and conditions"),
  stage("applying", "신청·진행 중", "Applying", "공식 신청이나 절차를 진행하는 단계", "Submitting or progressing the official process"),
  stage("stuck", "문제 발생", "Problem", "지연, 거절, 분쟁 또는 오류를 해결하는 단계", "Resolving a delay, refusal, dispute or error"),
  stage("solved", "해결·후기", "Resolved", "결과를 확인하고 이후 관리를 하는 단계", "Managing the outcome and next steps"),
];

export const AUTONOMOUS_TOPIC_EXTENSIONS: readonly TopicDefinition[] = [
  {
    id: "tax-return",
    label: "세금 신고",
    labelEn: "Tax Return",
    category: "money",
    description: "개인 소득·공제 자료 준비, myTax 신고와 amendment 안내",
    descriptionEn: "Preparing income and deduction records, lodging with myTax and amendments",
    keywords: ["tax return", "세금신고", "mytax", "deduction", "income"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "accounting",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["tax_agent", "accountant"],
  },
  {
    id: "superannuation",
    label: "Superannuation",
    labelEn: "Superannuation",
    category: "money",
    description: "Super 계좌, employer contribution, 보험, consolidation과 unpaid super 안내",
    descriptionEn: "Super accounts, employer contributions, insurance, consolidation and unpaid super",
    keywords: ["superannuation", "super", "연금", "employer contribution", "fund"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "finance",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["super_advisor", "tax_agent", "financial_adviser"],
  },
  // "pay-and-wages" and "rental-bond-disputes" used to be defined here as
  // thin stubs. Both ids now get their full TopicDefinition (with real
  // problem/situation/firstAction copy) from the corresponding
  // shared/topic-catalog-expansion.ts entry instead - that entry was
  // originally authored under the ids "unpaid-wages" and "bond-dispute"
  // without realizing these ids already existed here, so the two were
  // merged onto the ids already used by
  // shared/topic-content-production-queue.ts and the test suite. Removed
  // rather than left in place to avoid two definitions for the same id (the
  // catalog-expansion entry is spread into OZK_TOPICS before this file's
  // push-if-missing loop runs, so it would have been silently skipped
  // anyway - deleting it here just makes that explicit).
  {
    id: "childcare",
    label: "육아·Child Care",
    labelEn: "Child Care",
    category: "family",
    description: "Childcare service 찾기, CCS 신청과 ongoing management",
    descriptionEn: "Finding child care, claiming CCS and managing ongoing details",
    keywords: ["childcare", "child care", "ccs", "육아", "subsidy", "preschool"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "education",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["childcare_advisor", "family_payment_advisor"],
  },
  {
    id: "partner-visa",
    label: "파트너 비자",
    labelEn: "Partner Visa",
    category: "visa",
    description: "Onshore·offshore partner visa, relationship evidence와 sponsor 준비",
    descriptionEn: "Onshore and offshore partner visas, relationship evidence and sponsorship",
    keywords: ["partner visa", "배우자비자", "820", "801", "309", "100", "sponsor"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "legal",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["migration_agent", "migration_lawyer"],
  },
  {
    id: "skilled-migration",
    label: "기술이민",
    labelEn: "Skilled Migration",
    category: "visa",
    description: "SkillSelect, occupation, assessment, points, EOI와 nomination",
    descriptionEn: "SkillSelect, occupations, skills assessments, points, EOIs and nominations",
    keywords: ["skilled migration", "기술이민", "skillselect", "eoi", "points", "189", "190", "491"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "legal",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["migration_agent", "skills_assessment_advisor"],
  },
  {
    id: "unfair-dismissal",
    label: "부당해고",
    labelEn: "Unfair Dismissal",
    category: "work",
    description: "해고 기록, FWC 관할·기한과 application 준비",
    descriptionEn: "Dismissal records, FWC jurisdiction, time limits and applications",
    keywords: ["unfair dismissal", "부당해고", "fwc", "termination", "general protections"],
    stages: commonStages,
    defaultCommunityModes: ["question", "experience"],
    businessCategoryId: "legal",
    sourceTrust: 5,
    requiresFreshnessCheck: true,
    expertCategories: ["employment_lawyer", "workplace_advisor"],
  },
] as const;

for (const topic of AUTONOMOUS_TOPIC_EXTENSIONS) {
  if (!OZK_TOPICS.some((existing) => existing.id === topic.id)) {
    OZK_TOPICS.push(topic);
  }
}
